import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import WalletGearModal, { PageGearButton } from './WalletGearModal';
import OpenPositionsSection from './OpenPositionsSection';
import MultiAssetTradePanel, { useTradePanelScroll } from './MultiAssetTradePanel';
import CommissionOwedPanel from './CommissionOwedPanel';
import SignalQueueSection from './SignalQueueSection';
import OrderTransactionsSection from './OrderTransactionsSection';
import PaperTestBalanceSettings from './PaperTestBalanceSettings';
import { positionKey } from '../utils/tradeActionUi';
import { fmtUsd } from '../utils/fmtPrice';
import { isSessionAuthError, SESSION_AUTH_MESSAGE, truncateStatusMessage } from '../utils/apiAuth';
import './CommissionOwedPanel.css';
import './OBotTrading.css';
import './WalletGearModal.css';
import './OpenPositionsSection.css';
import './MultiAssetTradePanel.css';
import './OrderTransactionsSection.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const defaultForm = {
  symbol: 'GOLD',
  hlDex: 'xyz',
  hlCoin: null,
  leverage: 2,
  hlMaxLeverage: null,
  side: 'long',
  action: 'open',
  sizeUsd: 5000,
  stopPrice: '',
  targetPrice: '',
  reason: '',
};

export default function OBotTrading() {
  const { panelRef, scrollToPanel } = useTradePanelScroll();
  const [config, setConfig] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [closeQuickBusy, setCloseQuickBusy] = useState(false);
  const [positionsRefresh, setPositionsRefresh] = useState(0);
  const [obotLeveragePref, setObotLeveragePref] = useState(2);
  const [savingLeverage, setSavingLeverage] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const setPageMessage = (text, isError = false) => {
    const msg = truncateStatusMessage(text);
    if (isError) setError(msg);
    else setMessage(msg);
    if (isError) setMessage(null);
    else setError(null);
  };

  const load = useCallback(async () => {
    if (sessionExpired) return;
    setError(null);
    try {
      const [cfg, st] = await Promise.all([
        axios.get(`${API_BASE}/multi-asset/config`),
        axios.get(`${API_BASE}/multi-asset/status`, { headers: authHeaders() }),
      ]);
      setConfig(cfg.data);
      setBotStatus(st.data);
      const userLev = st.data.obotLeverage ?? cfg.data.strategyLeverage ?? 2;
      setObotLeveragePref(userLev);
      setForm((f) => ({ ...f, leverage: userLev }));
      const syms = cfg.data.allowedSymbols?.length
        ? cfg.data.allowedSymbols
        : cfg.data.knownSymbols || [];
      if (syms.length && !syms.includes(form.symbol)) {
        setForm((f) => ({ ...f, symbol: syms[0] }));
      }
    } catch (e) {
      if (isSessionAuthError(e)) {
        setSessionExpired(true);
        setError(SESSION_AUTH_MESSAGE);
      } else {
        setError(e.response?.data?.error || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [form.symbol, sessionExpired]);

  useEffect(() => {
    load();
    if (sessionExpired) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, sessionExpired]);

  useEffect(() => {
    const sym = form.symbol;
    const dex = form.hlDex || 'xyz';
    if (!sym) return;
    axios
      .get(`${API_BASE}/multi-asset/market-meta/${sym}`, {
        headers: authHeaders(),
        params: { dex },
      })
      .then(({ data }) => {
        setForm((f) => ({
          ...f,
          hlMaxLeverage: data.maxLeverage ?? null,
          hlDex: data.dex || f.hlDex,
          hlCoin: data.coin || f.hlCoin,
        }));
      })
      .catch(() => {});
  }, [form.symbol, form.hlDex]);

  const saveObotLeverage = async () => {
    setSavingLeverage(true);
    setError(null);
    try {
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/user/settings`,
        { obotLeverage: Number(obotLeveragePref) },
        { headers: authHeaders() }
      );
      setMessage(`Default oBot leverage set to ${data.obotLeverage}x.`);
      setForm((f) => ({ ...f, leverage: data.obotLeverage }));
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSavingLeverage(false);
    }
  };

  const applyPositionToForm = (p) => {
    setForm({
      ...defaultForm,
      symbol: p.symbol,
      leverage: obotLeveragePref,
      side: p.closeSide,
      action: 'close',
      sizeUsd: p.sizeUsd || 5000,
      reason: `Close ${p.side} ${p.symbol}`,
    });
  };

  const handleSelectPosition = (p) => {
    setSelectedPosition(p);
    applyPositionToForm(p);
    scrollToPanel();
  };

  const toggleBot = async (enable) => {
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE}/multi-asset/bot/${enable ? 'enable' : 'disable'}`,
        { paperMode: config?.paperMode !== false },
        { headers: authHeaders() }
      );
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/signals`,
        {
          symbol: form.symbol,
          side: form.side,
          action: form.action,
          sizeUsd: Number(form.sizeUsd),
          stopPrice: form.stopPrice ? Number(form.stopPrice) : undefined,
          targetPrice: form.targetPrice ? Number(form.targetPrice) : undefined,
          leverage: Number(form.leverage) || obotLeveragePref,
          tradingMode: 'obot',
          source: 'manual',
          reason: form.reason || 'Trade from oBot page',
        },
        { headers: authHeaders() }
      );
      setMessage(
        `Signal #${data.signalId} queued for oBot worker${data.paperMode ? ' (paper)' : ' (live — requires active oBot session)'}.`
      );
      setPositionsRefresh((k) => k + 1);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTradeLot = (p) => {
    setSelectedPosition(p);
    applyPositionToForm(p);
    scrollToPanel();
    const margin = p.marginUsd ?? p.sizeUsd ?? 0;
    const lev = p.leverage || 1;
    const total = p.notionalUsd != null ? p.notionalUsd : margin * lev;
    setPageMessage(
      p.side === 'long'
        ? `Partial sell — reduce ${p.symbol} long. Margin ${fmtUsd(margin)} · ${lev}x (≈ ${fmtUsd(total)}). Adjust margin below.`
        : `Partial buy — reduce ${p.symbol} short. Margin ${fmtUsd(margin)} · ${lev}x (≈ ${fmtUsd(total)}). Adjust margin below.`
    );
  };

  const handleCloseLot = async (p) => {
    setCloseQuickBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/liquidate`,
        { symbol: p.symbol, side: p.side, lotKey: p.lotKey, scope: 'obot' },
        { headers: authHeaders() }
      );
      setPageMessage(data.message || `Closed ${p.symbol}`);
      setPositionsRefresh((k) => k + 1);
      await load();
    } catch (err) {
      setPageMessage(err.response?.data?.error || err.message, true);
    } finally {
      setCloseQuickBusy(false);
    }
  };

  const closePositionQuick = async (p) => {
    if (!window.confirm(`Queue close for ${p.side} ${p.symbol}?`)) return;
    setCloseQuickBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/liquidate`,
        { symbol: p.symbol, side: p.side, lotKey: p.lotKey, scope: 'obot' },
        { headers: authHeaders() }
      );
      setMessage(data.message || `Close queued for ${p.symbol}`);
      setPositionsRefresh((k) => k + 1);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCloseQuickBusy(false);
    }
  };

  if (loading) return <div className="obot-page loading">Loading oBot…</div>;

  const userOn = !!botStatus?.user?.bot_enabled;
  const globalOn = !!botStatus?.global?.enabled;
  const paper = botStatus?.global?.paperMode !== false;
  const symbols = config?.allowedSymbols?.length
    ? config.allowedSymbols
    : config?.knownSymbols || [form.symbol];
  const obotMaxLev = config?.obotMaxLeverage ?? botStatus?.obotMaxLeverage ?? 2;
  const strategyLev = config?.strategyLeverage ?? 2;

  return (
    <div className="obot-page">
      <header className="obot-header page-header-with-gear">
        <div className="page-header-main">
          <p>
            <Link to="/multi-asset">← Multi-Asset Trading</Link>
          </p>
          <h1>oBot Trading</h1>
          <p className="subtitle">
            Automated Hyperliquid execution (tokenized stocks, indices, commodities, FX). Strategy:{' '}
            <strong>{config?.strategyName || 'omniMapSwing'}</strong> — Omni MAP swing thesis runs at{' '}
            <strong>{strategyLev}x</strong>. Trades you queue here can use up to{' '}
            <strong>{obotMaxLev}x</strong> (admin cap).
          </p>
        </div>
        <PageGearButton onClick={() => setWalletModalOpen(true)} label="oBot authorization" icon="wallet" />
      </header>

      <WalletGearModal
        open={walletModalOpen}
        onClose={() => setWalletModalOpen(false)}
        title="oBot authorization"
        view="obot"
      />

      {error && (
        <div className="obot-error">
          {error}
          <button type="button" className="obot-dismiss-btn" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {message && (
        <div className="obot-success">
          {message}
          <button type="button" className="obot-dismiss-btn" onClick={() => setMessage(null)}>
            Dismiss
          </button>
        </div>
      )}

      <CommissionOwedPanel refreshKey={positionsRefresh} onPaid={load} />

      {paper && (
        <section className="obot-card">
          <PaperTestBalanceSettings compact />
        </section>
      )}

      <section className="obot-card obot-status-grid">
        <div>
          <span className="label">Global bot</span>
          <span className={`pill ${globalOn ? 'on' : 'off'}`}>{globalOn ? 'ON' : 'OFF'}</span>
          <p className="hint">Admin tab → Multi-asset oBot settings.</p>
        </div>
        <div>
          <span className="label">Your subscription</span>
          <span className={`pill ${userOn ? 'on' : 'off'}`}>{userOn ? 'ENABLED' : 'DISABLED'}</span>
        </div>
        <div>
          <span className="label">Mode</span>
          <span className={`pill ${paper ? 'mode-paper' : 'mode-live'}`}>{paper ? 'PAPER' : 'LIVE'}</span>
        </div>
        <div>
          <span className="label">Your HL wallet</span>
          <span className="pill">
            {botStatus?.wallet?.walletAddress
              ? `${botStatus.wallet.walletAddress.slice(0, 6)}…${botStatus.wallet.walletAddress.slice(-4)}`
              : 'NOT LINKED'}
          </span>
        </div>
      </section>

      <section className="obot-card obot-leverage-settings">
        <h2>Your oBot trade leverage</h2>
        <p className="hint">
          Default leverage for trades you queue on this page (not automated MAP signals — those stay at{' '}
          {strategyLev}x). Admin cap: {obotMaxLev}x.
          {form.hlMaxLeverage != null && ` Hyperliquid max for ${form.symbol}: ${form.hlMaxLeverage}x.`}
        </p>
        <div className="obot-leverage-row">
          <label>
            Default leverage
            <select
              value={String(obotLeveragePref)}
              onChange={(e) => setObotLeveragePref(Number(e.target.value))}
            >
              {Array.from({ length: obotMaxLev }, (_, i) => i + 1).map((x) => (
                <option key={x} value={x}>
                  {x}x
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="secondary-btn"
            disabled={savingLeverage}
            onClick={saveObotLeverage}
          >
            {savingLeverage ? 'Saving…' : 'Save leverage'}
          </button>
        </div>
      </section>

      <OpenPositionsSection
        selectedPositionKey={selectedPosition ? positionKey(selectedPosition) : null}
        selectedSymbol={selectedPosition?.symbol}
        onSelectPosition={handleSelectPosition}
        onTradeLot={handleTradeLot}
        onCloseLot={handleCloseLot}
        onStatusMessage={setPageMessage}
        onPositionsChanged={() => setPositionsRefresh((k) => k + 1)}
        refreshKey={positionsRefresh}
        maxVisibleLots={5}
        scope="obot"
        title="Open positions (your oBot book)"
      />

      <MultiAssetTradePanel
        panelRef={panelRef}
        form={form}
        setForm={setForm}
        symbols={symbols}
        submitting={submitting}
        onSubmit={submit}
        selectedPosition={selectedPosition}
        onClosePositionQuick={closePositionQuick}
        closeQuickBusy={closeQuickBusy}
        tradingMode="obot"
        leverageCap={obotMaxLev}
        hlMaxLeverage={form.hlMaxLeverage}
        strategyLeverage={strategyLev}
      />

      <section className="obot-card obot-actions">
        <p className="hint">
          Link your HL wallet and approve the oBot agent via the gear icon →{' '}
          <strong>Start oBot</strong>. Cash reserve and paper balance are in Settings.
        </p>
        {!userOn && paper && (
          <button type="button" className="secondary-btn" disabled={busy} onClick={() => toggleBot(true)}>
            Enable paper processing for my account
          </button>
        )}
        <button type="button" className="secondary-btn" onClick={load}>
          Refresh
        </button>
      </section>

      {botStatus?.sampleMids && (
        <section className="obot-card">
          <h2>Hyperliquid mids (sample)</h2>
          <div className="mid-grid">
            {Object.entries(botStatus.sampleMids).map(([k, v]) => (
              <div key={k}>
                <strong>{k}</strong> {v != null ? v : '—'}
              </div>
            ))}
          </div>
          {botStatus.goldRule && (
            <p className={`gold-rule-status ${botStatus.goldRule.inBand ? 'gold-ready' : 'gold-wait'}`}>
              Gold add rule: price {botStatus.goldRule.price ?? '—'} · stop {botStatus.goldRule.stop ?? '—'} · addBelow{' '}
              {botStatus.goldRule.addBelow ?? '—'}
              {botStatus.goldRule.inBand
                ? ' · in band — algorithm should queue an add on next tick'
                : botStatus.goldRule.reason === 'above_addBelow'
                  ? ` · price above addBelow (${botStatus.goldRule.addBelow}) — raise addBelow in MAP JSON (e.g. 4500) to trigger`
                  : ` · waiting (${botStatus.goldRule.reason || 'unknown'})`}
              {botStatus.goldRule.blockedPending ? ' · blocked: pending GOLD signal already exists' : ''}
              {botStatus.goldRule.blockedCooldown ? ' · blocked: GOLD add cooldown (6h since last fill)' : ''}
            </p>
          )}
        </section>
      )}

      {botStatus?.isAdmin && !config?.uiHideObotSignalQueue && (
        <SignalQueueSection refreshKey={positionsRefresh} feed="obot" />
      )}

      {botStatus?.isAdmin && !config?.uiHideObotOrderTransactions && (
        <OrderTransactionsSection orders={botStatus?.recentOrders || []} />
      )}
    </div>
  );
}
